import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import dump from '@/images/dump.svg';
import pause from '@/images/pauseCircle.svg';
import play from '@/images/playCircle.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useAnalytics } from '@/services/analytics';
import { logger } from '@/services/logger';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import Slider from '@mui/material/Slider';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

const AudioPlayerContainer = styled.div`
    flex: 1;
    background: ${({ theme }) => theme.colors.black[50]};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    height: 100%;
`;

const AudioPlayerInnerContainer = styled.div`
    width: 60%;
    gap: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
`;

const AudioSlider = styled(Slider)`
    color: black;

    & > .MuiSlider-thumb {
        transition-duration: 15ms;
        color: white;

        &:hover {
            box-shadow: none;
        }
    }

    & > .MuiSlider-rail {
        color: ${({ theme }) => theme.colors.secondary.main}45;
    }

    & > .MuiSlider-track {
        transition-duration: 15ms;
        color: ${({ theme }) => theme.colors.secondary.main};
    }
`;

const DeleteTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        fontSize: 12,
    },
});

const PositionText = styled.p`
    font-size: 1.3rem;
`;

const VideoName = styled.span`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 300px;
`;

const DumpIconContainer = styled.div`
    background-color: white;
    border-radius: 50%;
    padding: 8px;
    cursor: pointer;
    height: 40px;
    width: 40px;
`;

const PPIcon = styled(MaskedIcon)`
    cursor: pointer;
`;

const SliderContainer = styled.div`
    flex: 1;
    margin-top: 8px;
`;

const SliderDurations = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const NameDeleteContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-size: 1.6rem;
    padding: 0 8px;
`;

const PlayerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    background-color: white;
    border-radius: 12px;
    padding: 8px;
    width: 100%;
`;

interface Props {
    className?: string;
    audioData: AudioData;
    onDelete: () => void;
}

const DEFAULT_AUDIO_NAME = 'audio.wav';

export const AudioPlayer = observer(({ audioData, onDelete, className }: Props) => {
    const mixpanel = useAnalytics();
    const { store } = useAdvancedEditorContext();
    const isScenesPreviewPlaying = store?.isPlaying;
    const audioRef = useRef<ReactPlayer>(null);
    const theme = useTheme();

    const [isPlaying, setIsPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (isScenesPreviewPlaying && isPlaying) {
            setIsPlaying(false);
        }
    }, [isScenesPreviewPlaying]);

    // Stop the audio when the page is changed
    useEffect(() => {
        if (store?.activePage?.id) {
            setIsPlaying(false);
            setPosition(0);
        }
    }, [store?.activePage?.id]);

    const toggleAudio = useCallback(() => {
        mixpanel?.track('audio', { event: 'click', isPlaying });
        if (isScenesPreviewPlaying) {
            store.stop();
        }
        setIsPlaying(isPlaying => !isPlaying);
    }, [isScenesPreviewPlaying]);

    const handleAudioError = useCallback((error: any) => {
        toast.error({
            toastId: 'uploaded-audio-error',
            title: 'Voice audio file is not available. Please reupload the file',
        });
        mixpanel?.track('audio', { event: 'error' });
        logger.error('Error playing audio', error);
        onDelete();
    }, []);

    return (
        <AudioPlayerContainer data-testid="script-tab-audio-player" className={className}>
            <AudioPlayerInnerContainer className="audio-player__inner-container">
                <NameDeleteContainer className="audio-player__delete-container">
                    <VideoName>{audioData.name !== DEFAULT_AUDIO_NAME ? audioData.name : ''}</VideoName>
                    <DeleteTooltip data-testid="script-delete-audio-button" title={'Delete Audio'} placement={'bottom'}>
                        <DumpIconContainer>
                            <MaskedIcon
                                src={dump}
                                color={theme.colors.error.main}
                                size="24px"
                                onClick={() => {
                                    onDelete();
                                }}
                            />
                        </DumpIconContainer>
                    </DeleteTooltip>
                </NameDeleteContainer>
                <PlayerContainer className="audio-player__player-container">
                    <PPIcon src={isPlaying ? pause : play} color={theme.colors.secondary.main} size="48px" onClick={toggleAudio} />

                    <SliderContainer>
                        <AudioSlider
                            min={0}
                            max={audioData?.duration}
                            step={0.001}
                            onChangeCommitted={(_, value) => {
                                value = value as number;
                                audioRef.current?.seekTo(value, 'seconds');
                                setPosition(value);
                                setIsDragging(false);
                            }}
                            onChange={(_, value) => {
                                setIsDragging(true);
                                setPosition(value as number);
                            }}
                            value={position}
                            aria-label="continuous-slider"
                        />
                        <SliderDurations>
                            <PositionText>{new Date((audioData.duration - position) * 1000).toISOString().substring(14, 19)}</PositionText>
                            <PositionText>{new Date(audioData.duration * 1000).toISOString().substring(14, 19)}</PositionText>
                        </SliderDurations>
                    </SliderContainer>
                    {isPlaying && (
                        <ReactPlayer
                            style={{ display: 'none' }}
                            url={audioData.listenUrl}
                            ref={audioRef}
                            controls={false}
                            playing={isPlaying}
                            progressInterval={1}
                            onProgress={state => !isDragging && setPosition(Math.ceil(state.playedSeconds * 1000) / 1000)}
                            onEnded={() => setIsPlaying(false)}
                            onError={error => {
                                handleAudioError(error);
                            }}
                        />
                    )}
                </PlayerContainer>
            </AudioPlayerInnerContainer>
        </AudioPlayerContainer>
    );
});

export default AudioPlayer;
